import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'What is automated customer service?',
    answer: 'Automated customer service uses AI and software to handle customer inquiries, support requests, and common questions without requiring a human agent for every interaction. It includes AI chatbots, automated email responses, self-service portals, and intelligent routing systems that resolve issues instantly — 24 hours a day, 7 days a week.'
  },
  {
    question: 'Will automated customer service feel impersonal to my customers?',
    answer: 'Not with modern AI. Our system uses natural language processing to understand context and respond in a conversational, personalized way — referencing the customer\'s name, their specific issue, and your business\'s tone. Most customers prefer getting an instant, accurate answer from AI over waiting hours for a human response.'
  },
  {
    question: 'What percentage of customer inquiries can AI handle automatically?',
    answer: 'For most small service businesses, AI can handle 70–85% of incoming customer inquiries automatically — things like hours, pricing, service availability, appointment status, FAQs, and basic troubleshooting. Complex or sensitive issues are escalated to your team with full context, so nothing falls through the cracks.'
  },
  {
    question: 'How does automated customer service handle complaints or upset customers?',
    answer: 'Our AI is trained to detect frustration and escalate appropriately. When a customer is upset or the issue requires human empathy and judgment, the system immediately routes the conversation to your team with the full conversation history. You can also set custom escalation rules based on keywords, sentiment, or issue type.'
  },
  {
    question: 'Can it handle customer service across multiple channels — text, email, and chat?',
    answer: 'Yes. Our automated customer service system works across SMS, email, website chat, and Facebook Messenger from a single unified platform. Customers get consistent, accurate responses regardless of which channel they use, and your team sees all conversations in one place.'
  },
  {
    question: 'How long does it take to set up automated customer service?',
    answer: 'Setup typically takes 24–48 hours. We train the AI on your business information, FAQs, services, and policies. You review and approve the responses before going live. Most businesses are fully operational within two business days with no technical work required on your end.'
  },
  {
    question: 'Does it integrate with my existing tools and CRM?',
    answer: 'Yes. Our system integrates with popular CRMs like HubSpot, GoHighLevel, and Salesforce, as well as scheduling tools, email platforms, and helpdesk software. Customer interactions are automatically logged, tickets are created, and your team always has full context without manual data entry.'
  },
  {
    question: 'How much does automated customer service cost for a small business?',
    answer: 'Our automated customer service is priced for small businesses — a fraction of the cost of hiring even one part-time customer service rep. You get 24/7 coverage, unlimited simultaneous conversations, and consistent quality at a predictable monthly rate. Most clients see a positive ROI within the first 30 days.'
  }
];

const stats = [
  { value: '85%', label: 'Of inquiries resolved without human involvement' },
  { value: '24/7', label: 'Coverage with zero overtime or staffing costs' },
  { value: '3 sec', label: 'Average AI response time to customer messages' },
  { value: '60%', label: 'Average reduction in customer service costs' },
];

const features = [
  {
    icon: 'ri-chat-smile-3-line',
    title: 'AI-Powered Chat Support',
    description: 'Handle website and SMS inquiries instantly with conversational AI that understands context and gives accurate, helpful answers every time.'
  },
  {
    icon: 'ri-mail-send-line',
    title: 'Automated Email Responses',
    description: 'Respond to customer emails within seconds with personalized, accurate replies — no more inbox backlog or delayed responses.'
  },
  {
    icon: 'ri-question-answer-line',
    title: 'Smart FAQ Automation',
    description: 'Train the AI on your most common questions and let it handle them automatically, freeing your team for complex, high-value interactions.'
  },
  {
    icon: 'ri-emotion-line',
    title: 'Sentiment Detection',
    description: 'AI detects frustrated or upset customers and escalates immediately to your team with full context — before the situation gets worse.'
  },
  {
    icon: 'ri-share-forward-line',
    title: 'Intelligent Escalation',
    description: 'Set custom rules for when AI hands off to humans. Complex issues, VIP customers, or specific keywords trigger instant team notifications.'
  },
  {
    icon: 'ri-global-line',
    title: 'Omnichannel Coverage',
    description: 'One system handles chat, SMS, email, and social DMs. Customers get consistent service on every channel, managed from a single dashboard.'
  },
  {
    icon: 'ri-links-line',
    title: 'CRM & Tool Integration',
    description: 'Automatically log every interaction, create support tickets, and sync customer data with your CRM — zero manual entry required.'
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Analytics & Reporting',
    description: 'Track resolution rates, response times, customer satisfaction, and common issues. Use real data to continuously improve your service.'
  }
];

const steps = [
  {
    number: '01',
    icon: 'ri-user-voice-line',
    title: 'Customer Reaches Out',
    description: 'A customer sends a message via chat, SMS, or email. The AI detects the inquiry instantly and begins crafting a response.'
  },
  {
    number: '02',
    icon: 'ri-brain-line',
    title: 'AI Understands & Responds',
    description: 'Using natural language processing, the AI understands the customer\'s intent and responds with an accurate, personalized answer within seconds.'
  },
  {
    number: '03',
    icon: 'ri-git-branch-line',
    title: 'Resolve or Escalate',
    description: 'Simple issues are resolved instantly. Complex or sensitive issues are escalated to your team with full conversation history and context.'
  },
  {
    number: '04',
    icon: 'ri-database-2-line',
    title: 'Log & Learn',
    description: 'Every interaction is logged in your CRM, tickets are created automatically, and the AI continuously improves from each conversation.'
  }
];

export default function AutomatedCustomerService() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="Automated Customer Service for Small Businesses | Dino Nuggets"
        description="Handle 85% of customer inquiries automatically with AI. Our automated customer service system responds in seconds via chat, SMS, and email — 24/7, without hiring more staff."
        canonical="/services/automated-customer-service"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'Automated Customer Service',
              description: 'AI-powered automated customer service system that handles inquiries via chat, SMS, and email 24/7 for small businesses.',
              provider: {
                '@type': 'Organization',
                name: 'Dino Nuggets',
                url: import.meta.env.VITE_SITE_URL || 'https://dinonuggets.ai'
              },
              areaServed: 'United States',
              availableChannel: {
                '@type': 'ServiceChannel',
                serviceUrl: `${import.meta.env.VITE_SITE_URL || 'https://dinonuggets.ai'}/services/automated-customer-service`
              }
            }
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer }
              }))
            }
          }
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/auto-cs-hero-001.jpg"
              alt="Automated Customer Service Technology"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/70"></div>

          <div className="relative w-full max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-customer-service-2-line"></i>
              Resolves 85% of inquiries automatically
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Automated Customer Service That Actually Works
            </h1>
            <p className="text-xl text-white/85 mb-10 leading-relaxed max-w-3xl mx-auto">
              Stop spending hours answering the same questions. Our AI handles customer inquiries via chat, SMS, and email in seconds — 24/7 — so your team can focus on the work that actually grows your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Automate My Customer Service
              </button>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                See How It Works
              </button>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-secondary py-12 px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm leading-snug">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* What Is It */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                What Is Automated Customer Service?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Automated customer service uses artificial intelligence to handle customer inquiries, answer questions, resolve issues, and provide support — without requiring a human agent for every single interaction. It works across <Link to="/services/ai-chatbot" className="text-primary hover:underline font-medium">chat</Link>, SMS, email, and social media, responding in seconds at any hour of the day.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                For small businesses, the customer service burden is real. You're fielding the same questions over and over — "What are your hours?", "How much does it cost?", "Can I reschedule my appointment?" — while trying to run your actual business. Every minute spent on repetitive support is a minute not spent on growth.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our automated customer service system handles up to <strong>85% of incoming inquiries automatically</strong>, with responses that feel personal and accurate. Complex issues, complaints, and high-value customers are escalated to your team instantly — with full context — so nothing important ever gets missed. For the inquiries it can't handle, our <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI receptionist</Link> ensures a seamless handoff.
              </p>
            </div>

            {/* How It Works */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-10">
                How It Works
              </h2>
              <div className="space-y-6">
                {steps.map((step) => (
                  <div key={step.number} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl">
                      <i className={`${step.icon} text-2xl text-primary`}></i>
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-bold text-primary tracking-widest">{step.number}</span>
                        <h3 className="font-semibold text-lg text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features Grid */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Everything Included in Automated Customer Service
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                A complete system built for small businesses that need professional support without the overhead:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {features.map((feature) => (
                  <div key={feature.title} className="border border-gray-200 p-6 rounded-lg hover:border-primary/40 transition-colors">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                      <i className={`${feature.icon} text-xl text-primary`}></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* The Real Cost of Manual Support */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-10 rounded-2xl">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                The Real Cost of Manual Customer Service
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                Most small business owners don't realize how much time and money they're losing to manual customer service. Here's what the numbers actually look like:
              </p>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-xl border border-gray-200 text-center">
                  <div className="text-3xl font-bold text-red-500 mb-2">3–5 hrs</div>
                  <div className="text-sm text-gray-600">Per day spent answering repetitive customer questions</div>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-200 text-center">
                  <div className="text-3xl font-bold text-amber-500 mb-2">$35k+</div>
                  <div className="text-sm text-gray-600">Annual cost of a part-time customer service employee</div>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-200 text-center">
                  <div className="text-3xl font-bold text-primary mb-2">67%</div>
                  <div className="text-sm text-gray-600">Of customers leave if they don't get a fast response</div>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Automated customer service eliminates all three problems at once. You stop losing hours to repetitive questions, you stop paying for coverage you can't afford, and you stop losing customers to slow response times — all for a fraction of what a single employee costs.
              </p>
            </div>

            {/* Industries */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Perfect for Service-Based Small Businesses
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Any business that fields repetitive customer questions benefits immediately. Industries we serve:
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-home-gear-line', label: 'HVAC & Home Services' },
                  { icon: 'ri-tools-line', label: 'Contractors & Builders' },
                  { icon: 'ri-plant-line', label: 'Landscaping & Lawn Care' },
                  { icon: 'ri-building-2-line', label: 'Roofing Companies' },
                  { icon: 'ri-heart-pulse-line', label: 'Medical & Dental Practices' },
                  { icon: 'ri-store-2-line', label: 'Retail & E-commerce' },
                  { icon: 'ri-scales-3-line', label: 'Law Firms' },
                  { icon: 'ri-scissors-line', label: 'Salons & Spas' },
                  { icon: 'ri-restaurant-line', label: 'Restaurants & Catering' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="w-8 h-8 flex items-center justify-center text-primary">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <span className="text-gray-800 text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Services */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Build a Complete AI Support Stack
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Combine automated customer service with these tools for end-to-end automation:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <Link
                  to="/services/ai-lead-follow-up"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                >
                  <i className="ri-send-plane-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    AI Lead Follow-Up
                  </h3>
                  <p className="text-sm text-gray-600">
                    Automatically follow up with new leads in 60 seconds before they go cold.
                  </p>
                </Link>
                <Link
                  to="/services/ai-chatbot"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                >
                  <i className="ri-chat-smile-3-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    AI Chatbot
                  </h3>
                  <p className="text-sm text-gray-600">
                    Capture and qualify leads from your website around the clock.
                  </p>
                </Link>
                <Link
                  to="/services/ai-receptionist"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                >
                  <i className="ri-customer-service-2-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    AI Receptionist
                  </h3>
                  <p className="text-sm text-gray-600">
                    Handle inbound calls with a virtual receptionist that never misses a beat.
                  </p>
                </Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">
                Ready to Stop Answering the Same Questions?
              </h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Let AI handle the repetitive stuff so you can focus on what actually moves the needle. Get 24/7 automated customer service set up in 48 hours.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="inline-block bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Get Automated Customer Service
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />

        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
